import {
  Box,
  Checkbox,
  Chip,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
  Link as Links,
  Tooltip,
} from "@mui/material";
import React, { useMemo } from "react";
import {
  DashBoardDateRangePicker,
  DeleteModal,
  InputControl,
  PageListLayout,
  ServerPaginatedtable,
} from "../../../shared";
import { createColumnHelper } from "@tanstack/react-table";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useIngredients } from "../../Ingredients/hooks/UseIngredient";
import { useImmer } from "use-immer";
import { useProductionChart } from "../hooks/useProductionChart";
import { addDays } from "date-fns";
import { navigateBasedOnUserType } from "../../../utils";

export const FutureChartList = () => {
  const {
    productionchart,
    futureListQuery: { isLoading, data },
    dataList,
    setPagination,
    pagination,
    sorting,
    setSorting,
    searchHandler,
    storeList,
    handleStoreSelect,
    onChangeDate,
    onChangeDateFuture,
    handleDateChangeApply,
    handleIsOpen,
  } = useProductionChart({ futureList: true });

  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");

  const navigate = useNavigate();
  const filter = createFilterOptions();

  const columnHelper = createColumnHelper(data?.data ?? []);
  const ActionButtons = ({ row }) => {
    return (
      <div className="action-buttons" onClick={(e) => e.preventDefault()}>
        <Stack
          direction="row"
          gap={1}
          justifyContent="flex-end"
          alignItems="center"
        >
          {!isStoreAdmin && (
            <Tooltip
              placement="bottom"
              arrow
              title={row.original.store_name
                .map((val) => {
                  return val.name;
                })
                .join(", ")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <img
                alt="edit"
                src="/icons/sm-home.png"
                style={{ padding: "2px" }}
              />
              <Typography
                lineHeight="16px"
                color="#657083"
                fontWeight={400}
                fontSize="14px"
              >
                {row.original.store_count} Stores
              </Typography>
            </Tooltip>
          )}

          <Typography
            lineHeight="16px"
            color="#657083"
            fontWeight={400}
            fontSize="18px"
          >
            &#183;
          </Typography>
          <Typography
            lineHeight="16px"
            color="#657083"
            fontWeight={400}
            fontSize="14px"
          >
            {row.original.date}
          </Typography>
        </Stack>
      </div>
    );
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("templates", {
        header: "",
        cell: ({ row }) => {
          let tags = row.original.templates;
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
              }}
            >
              {tags?.slice(0, 8).map((data, i) => {
                return (
                  <Chip
                    key={i}
                    sx={{
                      cursor: "pointer",
                      borderColor: "#C4CFE4",
                      fontWeight: 500,
                      fontSize: "12px",
                      color: "#1760D2",
                    }}
                    label={data.name}
                    variant="outlined"
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.preventDefault();
                      navigateBasedOnUserType(
                        `/templates/details/${data.id}`,
                        isStoreAdmin,
                        navigate
                      );
                    }}
                  />
                );
              })}
              {tags.length > 8 && (
                <Tooltip
                  placement="bottom"
                  arrow
                  title={
                    <>
                      {" "}
                      {tags?.slice(8, tags?.length).map((val, index) => {
                        return <div key={index}>{val.name}</div>;
                      })}
                    </>
                  }
                >
                  <Chip
                    sx={{
                      borderRadius: "15px",
                      height: "20px",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                    label={
                      `+${tags?.length - tags?.slice(0, 8)?.length}` + " More"
                    }
                    variant="outlined"
                    size="small"
                    color="gray2"
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
        enableSorting: false,
      }),

      columnHelper.display({
        id: "actions",
        cell: ({ row }) => <ActionButtons row={row} />,
      }),
    ],
    []
  );

  const breadcrumbs = [
    <Links
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) =>
        navigateBasedOnUserType("/production-charts", isStoreAdmin, navigate)
      }
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Production Chart
    </Links>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Future Charts
    </Typography>,
  ];

  return (
    <>
      <PageListLayout breadcrumbs={breadcrumbs} sx={{ p: 0, pb: "8px" }}>
        <ServerPaginatedtable
          defaultData={data?.data ?? []}
          columns={columns}
          hideColumnHeader
          onSearchChange={searchHandler}
          search={productionchart.filters.search}
          sorting={sorting}
          setSorting={setSorting}
          pagination={pagination}
          paginationProps={data?.pagination || { total: 0, lastPage: 1 }}
          setPagination={setPagination}
          headerText={"Future Charts"}
          isLoading={isLoading}
          rightContent={<Stack direction="row" gap="8px"></Stack>}
          tableHeaderContent={
            <Stack
              sx={{
                flexDirection: "row",
                gap: "8px",
                alignItems: "center",
              }}
            >
              {!isStoreAdmin && (
                <InputControl
                  type="dropdown"
                  fullWidth
                  multiple={true}
                  options={storeList?.data || []}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  disableCloseOnSelect
                  sx={{
                    maxWidth: "250px",
                    minWidth: "fit-content",
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => <></>)
                  }
                  value={productionchart.filters.stores}
                  onChange={(e, v) => handleStoreSelect(e, v)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.id}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.name}
                    </li>
                  )}
                  filterOptions={filter}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Store" size="small" />
                  )}
                />
              )}
              <DashBoardDateRangePicker
                onChangeDate={onChangeDate}
                range={productionchart.tempdate}
                showRange={productionchart.filters.date}
                isOpen={productionchart.isDatePickerOpen}
                handleIsOpen={handleIsOpen}
                handleDateChangeApply={handleDateChangeApply}
                text={"Date"}
                isInverted={true}
                minDate={addDays(new Date(), 7)}
              />
            </Stack>
          }
        />
      </PageListLayout>
    </>
  );
};
